import { BridgeClient } from 'app/service/bridge/BridgeClient';

const MODES = new Set(['legacy_indexed', 'contextual_net_gross']);
const TRUSTED_ERROR_CODES = new Set([
  'INVALID_PAYLOAD',
  'PERSISTENCE_FAILED',
  'UNKNOWN_ACTION',
]);

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value, expectedKeys) {
  return isRecord(value)
    && Object.keys(value).sort().join('\u0000') === expectedKeys.slice().sort().join('\u0000');
}

function invalidResponseError() {
  const error = new Error('The plugin returned an invalid contextual pricing state.');
  error.code = 'INVALID_CONTEXTUAL_PRICING_STATE';
  return error;
}

function unwrapBridgeResponse(response) {
  if (!isRecord(response) || !Object.prototype.hasOwnProperty.call(response, 'success')) {
    return response;
  }
  if (response.success !== true) throw invalidResponseError();
  const { success, ...payload } = response;
  return payload;
}

export function normalizeContextualPricingState(rawResponse) {
  const response = unwrapBridgeResponse(rawResponse);
  if (isRecord(response) && response.result === 'error') {
    const error = new Error('Contextual pricing request failed.');
    error.code = TRUSTED_ERROR_CODES.has(response.code) ? response.code : 'REQUEST_FAILED';
    throw error;
  }
  if (!hasExactKeys(response, ['contextual_pricing', 'result']) || response.result !== 'ok') {
    throw invalidResponseError();
  }
  const state = response.contextual_pricing;
  if (
    !hasExactKeys(state, ['enabled', 'mode'])
    || typeof state.enabled !== 'boolean'
    || !MODES.has(state.mode)
    || state.enabled !== (state.mode === 'contextual_net_gross')
  ) {
    throw invalidResponseError();
  }

  return state;
}

export function createBridgeContextualPricingApi({ client = new BridgeClient() } = {}) {
  const request = async (action, payload) => normalizeContextualPricingState(
    await client.sendMessageWithResponse(action, payload),
  );

  return {
    getStatus: () => request('contextual_pricing.status', {}),
    setEnabled: enabled => request('contextual_pricing.set_mode', {
      mode: enabled ? 'contextual_net_gross' : 'legacy_indexed',
    }),
  };
}
